import React from "react";

/**
 * Top-level error boundary. Prevents the entire app from white-screening
 * when a child component throws during render — shows a friendly fallback
 * with a "reload" action instead.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, errorMessage: (err && err.message) || "" };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, info);
  }

  reset = () => {
    this.setState({ hasError: false, errorMessage: "" });
    // Soft reload so the route re-mounts cleanly
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
            <div className="text-5xl mb-3">⚠️</div>
            <h1 className="heading text-xl font-bold">Something went wrong</h1>
            <p className="text-sm text-slate-500 mt-2">
              The page hit an unexpected error and could not be displayed. Please refresh — if it keeps happening, contact admin.
            </p>
            {this.state.errorMessage && (
              <pre className="mt-3 text-[10px] text-slate-400 text-left bg-slate-50 p-2 rounded border border-slate-100 overflow-auto max-h-24" data-testid="error-boundary-detail">
                {this.state.errorMessage}
              </pre>
            )}
            <button
              onClick={this.reset}
              className="mt-5 bg-brand hover:bg-[#172b66] text-white font-semibold rounded-lg px-5 py-2 text-sm"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
